import { lazy, ComponentType } from 'react';
import { SESSION_STORAGE_KEY } from '../constant/auth.constant';

/**
 * Key used in sessionStorage to track whether we've already attempted
 * a reload for a specific chunk that failed to load.
 */
const RETRY_STORAGE_KEY = SESSION_STORAGE_KEY.CHUNK_RETRY_RELOADED;

/**
 * Detects whether an error is a chunk/module load failure.
 * This covers Vite's dynamic import errors and webpack chunk loading errors.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && message.includes('failed to fetch'))
  );
};

/**
 * Wraps a dynamic import function with retry logic for chunk loading failures.
 *
 * After a new deployment, Vite generates new hashed chunk filenames.
 * Users with cached HTML still reference old chunk URLs that no longer exist
 * on the server, causing "Failed to fetch dynamically imported module" errors.
 *
 * This utility:
 * 1. Attempts the dynamic import
 * 2. If it fails with a chunk load error, forces a full page reload (once)
 *    to fetch the latest HTML with correct chunk references
 * 3. Uses sessionStorage to prevent infinite reload loops
 *
 * @param importFn - A function that returns a dynamic import promise
 * @param retryKey - Optional unique key to track retries per route/module
 * @returns A React.lazy-compatible component
 *
 * @example
 * // Instead of:
 * const Dashboard = lazy(() => import('../pages/Dashboard'));
 *
 * // Use:
 * const Dashboard = lazyRetry(() => import('../pages/Dashboard'));
 */
export const lazyRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retryKey?: string,
): React.LazyExoticComponent<T> => {
  return lazy(() => retryDynamicImport(importFn, retryKey));
};

/**
 * Wraps a dynamic import with chunk-load retry logic.
 * Can be used directly with React.lazy() or with named exports.
 *
 * @example
 * // Named export usage:
 * const Dashboard = lazy(() =>
 *   retryDynamicImport(
 *     () => import('../pages').then(m => ({ default: m.Dashboard })),
 *     'dashboard'
 *   )
 * );
 */
/**
 * Derives a stable per-chunk storage key when the caller doesn't pass a
 * `retryKey`. Without this every `lazyRetry()` call site would share ONE
 * global flag, so a single permanently-broken chunk would consume the one
 * allowed retry for every other route too.
 *
 * `importFn.toString()` contains the module/chunk path in both dev and
 * bundled output, so it is stable across reloads of the same build — which
 * is exactly the lifetime the flag needs to survive.
 */
const deriveRetryKey = (importFn: () => unknown): string => {
  const source = String(importFn);
  let hash = 0;
  for (let i = 0; i < source.length; i++) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return Math.abs(hash).toString(36);
};

export const retryDynamicImport = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retryKey?: string,
): Promise<{ default: T }> => {
  const storageKey = `${RETRY_STORAGE_KEY}_${retryKey || deriveRetryKey(importFn)}`;

  return importFn()
    .then((mod) => {
      // Loaded successfully — drop the latch so that a LATER deployment is
      // still allowed its own single recovery reload for this chunk.
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // sessionStorage can throw in private-mode/blocked-storage contexts;
        // failing to clear the latch must never break a successful import.
      }
      return mod;
    })
    .catch((error: unknown) => {
      // Only handle chunk load errors
      if (!isChunkLoadError(error)) {
        throw error;
      }

      let hasReloaded = false;
      try {
        hasReloaded = sessionStorage.getItem(storageKey) !== null;
      } catch {
        // If storage is unreadable we cannot prove this is a first attempt.
        // Treat it as "already retried" — surfacing an error screen is far
        // safer than risking an unbounded reload loop.
        hasReloaded = true;
      }

      if (!hasReloaded) {
        try {
          sessionStorage.setItem(storageKey, 'true');
        } catch {
          // Can't persist the latch across the reload, so don't reload at all
          // — otherwise every reload would look like a first attempt.
          throw error;
        }

        // One full page reload, to pick up a fresh index.html whose chunk
        // hashes match what is actually deployed (the stale-deploy case).
        window.location.reload();

        // Never-resolving promise so nothing renders while the reload runs.
        return new Promise<{ default: T }>(() => {});
      }

      // The reload already happened and the chunk STILL will not load, so it
      // is genuinely missing rather than stale. Keep the latch set (do NOT
      // clear it — clearing is what turned this into an infinite reload
      // loop) and let the error boundary render a real error screen.
      throw error;
    });
};

/**
 * Clears all chunk retry flags from sessionStorage.
 *
 * WARNING: do NOT call this on app mount/boot. The latch set by
 * `retryDynamicImport` has to survive the reload it triggers; wiping it
 * during boot makes every attempt look like the first one, which reloads
 * forever whenever a chunk is permanently missing. `retryDynamicImport`
 * already clears its own key on a successful import.
 *
 * Intended only for an explicit user-initiated "reset" action.
 */
export const clearChunkRetryFlags = (): void => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(RETRY_STORAGE_KEY)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};
